import React, { useEffect, useState } from 'react';
import { Form, Card } from 'react-bootstrap';
import { Multiselect } from 'multiselect-react-dropdown';
import ReactQuill from 'react-quill';
import {
  levelsOfPlay,
  playerAnalysisFeature,
  playerEnglishSkills,
  playerPhysicalFeature,
} from './selectObjects';
import {
  basketballPositions,
  soccerPositions,
  voleyballPositions,
  beachVolleyballPositions,
  TrackAndFieldPositions,
  iceHockeyPositions,
  waterPoloPositions,
  sports,
  years,
  genders,
} from '../filterBar/filters';

import 'react-quill/dist/quill.snow.css';
import './CreatePlayerForm.scss';

/* ─── Reusable tiny components ─── */
const SectionHeader = ({ icon, title, subtitle }) => (
  <div className="cpf-section__header">
    <div className="cpf-section__icon">
      <i className={`fas ${icon}`} />
    </div>
    <div>
      <h3 className="cpf-section__title">{title}</h3>
      {subtitle && <p className="cpf-section__subtitle">{subtitle}</p>}
    </div>
  </div>
);

const FieldError = ({ message }) =>
  message ? <span className="cpf-field__error">{message}</span> : null;

const FieldGroup = ({ label, required, children, error }) => (
  <div className="cpf-field">
    <label className="cpf-field__label">
      {label}
      {required && <span className="cpf-field__required">*</span>}
    </label>
    {children}
    <FieldError message={error} />
  </div>
);

/* Free-text entries added next to a predefined multiselect */
const CustomEntries = ({
  fields,
  append,
  remove,
  register,
  name,
  placeholder,
}) => (
  <div className="cpf-custom-entries">
    {fields.map((item, index) => (
      <div className="cpf-custom-entries__item" key={item.id}>
        <input
          className="cpf-input"
          ref={register()}
          name={`${name}[${index}]`}
          defaultValue={item.value}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="cpf-remove-btn"
          onClick={() => remove(index)}
          title="Remove"
        >
          <i className="fas fa-trash-alt" />
        </button>
      </div>
    ))}
    <button type="button" className="cpf-add-btn" onClick={() => append({})}>
      <i className="fas fa-plus" /> Add custom
    </button>
  </div>
);

/* ─── Main form ─── */
const CreatePlayerForm = ({
  fields,
  append,
  prepend,
  remove,
  register,
  handleSubmit,
  errors,
  createPlayer,
  selectedSport,
  isTransfer,
  updatePlayer,
  isEditMode,
  player,
  physicalFeatures,
  checkIfPhotoExists,
  multiselectAnalysisRef,
  multiselectPhysicalsRef,
  multiselectEnglishRef,
  multiSelectLevelOfPlayRef,
  accomplishmentsFields,
  accomplishmentsAppend,
  accomplishmentsRemove,
  customPhysicalsFields,
  customPhysicalsAppend,
  customPhysicalsRemove,
  customAnalysisFields,
  customAnalysisAppend,
  customAnalysisRemove,
  analysisFeatures,
  positionRef,
}) => {
  const [additionalComments, setAdditionalComments] = useState('');

  useEffect(() => {
    if (!isEditMode) return;
    player.additionalComments &&
      setAdditionalComments(player.additionalComments);
  }, [isEditMode, player]);

  const multiselectStyle = {
    chips: {
      background: '#2e496b',
      fontSize: '0.75rem',
      whiteSpace: 'pre-wrap',
      borderRadius: '6px',
      padding: '4px 10px',
    },
    searchBox: {
      background: '#fff',
      borderRadius: '10px',
      border: '1.5px solid #e0e0e0',
      padding: '6px 10px',
      minHeight: '42px',
      fontSize: '0.85rem',
    },
    optionContainer: {
      borderRadius: '10px',
      border: '1px solid #e0e0e0',
      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
      marginTop: '4px',
    },
    option: {
      fontSize: '0.85rem',
      padding: '8px 12px',
    },
  };

  const multiselectStyleSingle = {
    ...multiselectStyle,
    chips: {
      ...multiselectStyle.chips,
      color: 'white',
    },
  };

  // Keep an edited player's current year selectable even if it's no longer offered
  const currentYear = isEditMode && player?.graduationYear;
  const yearOptions =
    currentYear && !years.map(String).includes(String(currentYear))
      ? [currentYear, ...years]
      : years;

  const getOptions = () => {
    switch (selectedSport) {
      case 'Volleyball':
        return voleyballPositions;
      case 'Beach Volleyball':
        return beachVolleyballPositions;
      case 'Basketball':
        return basketballPositions;
      case 'Soccer':
        return soccerPositions;
      case 'Track & Field':
        return TrackAndFieldPositions;
      case 'Ice Hockey':
        return iceHockeyPositions;
      case 'Water Polo':
        return waterPoloPositions;
      default:
        return [];
    }
  };

  const handleCommentsChange = content => {
    setAdditionalComments(content);
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['blockquote'],
      ['link'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'blockquote',
    'link',
  ];

  return (
    <div className="cpf-wrapper">
      {/* ─── Page header ─── */}
      <div className="cpf-page-header">
        <div className="cpf-page-header__icon">
          <i className={`fas ${isEditMode ? 'fa-user-edit' : 'fa-user-plus'}`} />
        </div>
        <div>
          <h1 className="cpf-page-header__title">
            {isEditMode ? 'Edit Player' : 'Add New Player'}
          </h1>
          <p className="cpf-page-header__subtitle">
            {isEditMode
              ? 'Update the player\'s information below'
              : 'Fill in the details to create a new player profile'}
          </p>
        </div>
      </div>

      <Form
        onSubmit={handleSubmit(data => {
          const formData = { ...data, additionalComments };
          !isEditMode
            ? handleSubmit(createPlayer(formData))
            : handleSubmit(updatePlayer(formData));
        })}
      >
        <div className="cpf-layout">
          {/* ════════════ LEFT COLUMN ════════════ */}
          <div className="cpf-layout__main">

            {/* ── Admin toggles ── */}
            <div className="cpf-section cpf-section--toggles">
              <div className="cpf-toggles">
                <label className="cpf-toggle">
                  <Form.Check
                    type="checkbox"
                    name="hidden"
                    ref={register}
                    className="cpf-toggle__input"
                  />
                  <span className="cpf-toggle__label">
                    <i className="fas fa-eye-slash" /> Hide Player
                  </span>
                </label>
                <label className="cpf-toggle">
                  <Form.Check
                    type="checkbox"
                    name="transfer"
                    ref={register}
                    className="cpf-toggle__input"
                  />
                  <span className="cpf-toggle__label">
                    <i className="fas fa-exchange-alt" /> Transfer
                  </span>
                </label>
              </div>
            </div>

            {/* ── Personal Information ── */}
            <div className="cpf-section">
              <SectionHeader icon="fa-user" title="Personal Information" subtitle="Basic identity details" />
              <div className="cpf-grid cpf-grid--2">
                <FieldGroup label="First Name" required error={errors?.firstName?.message}>
                  <input
                    className={`cpf-input ${errors.firstName ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="firstName"
                    placeholder="e.g. John"
                  />
                </FieldGroup>
                <FieldGroup label="Last Name" required error={errors?.lastName?.message}>
                  <input
                    className={`cpf-input ${errors.lastName ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="lastName"
                    placeholder="e.g. Smith"
                  />
                </FieldGroup>
                <FieldGroup label="Gender" required error={errors?.gender?.message}>
                  <select
                    className={`cpf-input cpf-select ${errors.gender ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="gender"
                  >
                    {genders.map(gen => (
                      <option value={gen} key={gen}>{gen}</option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup label="Date of Birth" required error={errors?.dateOfBirth?.message}>
                  <input
                    type="date"
                    className={`cpf-input ${errors.dateOfBirth ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="dateOfBirth"
                  />
                </FieldGroup>
                <FieldGroup label="Nationality" required error={errors?.nationality?.message}>
                  <input
                    className={`cpf-input ${errors.nationality ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="nationality"
                    placeholder="e.g. Polish"
                  />
                </FieldGroup>
                <FieldGroup label="Sport" required error={errors?.sport?.message}>
                  <select
                    className={`cpf-input cpf-select ${errors.sport ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="sport"
                  >
                    {sports.map(sport => (
                      <option value={sport} key={sport}>{sport}</option>
                    ))}
                  </select>
                </FieldGroup>
              </div>
            </div>

            {/* ── Contact ── */}
            <div className="cpf-section">
              <SectionHeader icon="fa-envelope" title="Contact" subtitle="Email & phone" />
              <div className="cpf-grid cpf-grid--2">
                <FieldGroup label="Email Address" required error={errors?.email?.message}>
                  <input
                    className={`cpf-input ${errors.email ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="email"
                    placeholder="player@email.com"
                  />
                </FieldGroup>
                <FieldGroup label="Phone Number" error={errors?.phoneNumber?.message}>
                  <input
                    className={`cpf-input ${errors.phoneNumber ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="phoneNumber"
                    placeholder="+1 234 567 890"
                  />
                </FieldGroup>
              </div>
            </div>

            {/* ── Academic ── */}
            <div className="cpf-section">
              <SectionHeader icon="fa-graduation-cap" title="Academic" subtitle="School & test scores" />
              <div className="cpf-grid cpf-grid--2">
                <FieldGroup label={isTransfer ? 'University Name' : 'High School Name'} error={errors?.currentSchool?.message}>
                  <input
                    className={`cpf-input ${errors.currentSchool ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="currentSchool"
                    placeholder={isTransfer ? 'e.g. University of Texas' : 'e.g. SMS Szczyrk'}
                  />
                </FieldGroup>
                <FieldGroup label="Current Club" required error={errors?.currentClub?.message}>
                  <input
                    className={`cpf-input ${errors.currentClub ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="currentClub"
                    placeholder="e.g. KS Warta"
                  />
                </FieldGroup>
                <FieldGroup label="Available From" required error={errors?.graduationYear?.message}>
                  <select
                    className={`cpf-input cpf-select ${errors.graduationYear ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="graduationYear"
                  >
                    {yearOptions.map(year => (
                      <option value={year} key={year}>{year}</option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup label={isTransfer ? "University GPA" : "High School GPA"} error={errors?.highSchoolGpa?.message}>
                  <input
                    className={`cpf-input ${errors.highSchoolGpa ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="highSchoolGpa"
                    placeholder="e.g. 3.8"
                  />
                </FieldGroup>
                <FieldGroup label="SAT Score" error={errors?.satScore?.message}>
                  <input
                    className={`cpf-input ${errors.satScore ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="satScore"
                    placeholder="e.g. 1200"
                  />
                </FieldGroup>
                <FieldGroup label="TOEFL Score" error={errors?.toeflScore?.message}>
                  <input
                    className={`cpf-input ${errors.toeflScore ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="toeflScore"
                    placeholder="e.g. 90"
                  />
                </FieldGroup>
                <FieldGroup label="Duolingo Score">
                  <input
                    className="cpf-input"
                    ref={register}
                    name="duolingoScore"
                    placeholder="e.g. 115"
                  />
                </FieldGroup>
                <FieldGroup label="Intended Major" error={errors?.intendedMajor?.message}>
                  <input
                    className={`cpf-input ${errors.intendedMajor ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="intendedMajor"
                    placeholder="e.g. Business"
                  />
                </FieldGroup>
                <FieldGroup label="Budget" error={errors?.budget?.message}>
                  <input
                    className={`cpf-input ${errors.budget ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="budget"
                    placeholder="e.g. $15,000 - $25,000"
                  />
                </FieldGroup>
              </div>

              {/* Accomplishments */}
              <div className="cpf-dynamic-list">
                <div className="cpf-dynamic-list__header">
                  <label className="cpf-field__label">
                    <i className="fas fa-trophy" style={{ marginRight: '0.5rem', color: '#cc3d45' }} />
                    Accomplishments
                  </label>
                  <button
                    type="button"
                    className="cpf-add-btn"
                    onClick={() => accomplishmentsAppend({})}
                  >
                    <i className="fas fa-plus" /> Add
                  </button>
                </div>
                {accomplishmentsFields.map((item, index) => (
                  <div className="cpf-dynamic-list__item" key={item.id}>
                    <textarea
                      className="cpf-input cpf-textarea"
                      ref={register()}
                      name={`accomplishments[${index}]`}
                      defaultValue={item.value}
                      placeholder="Describe an accomplishment..."
                      rows={2}
                    />
                    <button
                      type="button"
                      className="cpf-remove-btn"
                      onClick={() => accomplishmentsRemove(index)}
                      title="Remove"
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  </div>
                ))}
                {accomplishmentsFields.length === 0 && (
                  <p className="cpf-dynamic-list__empty">No accomplishments added yet.</p>
                )}
              </div>
            </div>

            {/* ── Athletic Profile ── */}
            <div className="cpf-section">
              <SectionHeader icon="fa-running" title="Athletic Profile" subtitle="Physical stats & position" />
              <div className="cpf-grid cpf-grid--2">
                <FieldGroup label="Height" required error={errors?.height?.message}>
                  <input
                    className={`cpf-input ${errors.height ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="height"
                    placeholder={"e.g. 6'2 / 188cm"}
                  />
                </FieldGroup>
                <FieldGroup label="Weight" required error={errors?.weight?.message}>
                  <input
                    className={`cpf-input ${errors.weight ? 'cpf-input--error' : ''}`}
                    ref={register}
                    name="weight"
                    placeholder="e.g. 180lbs / 82kg"
                  />
                </FieldGroup>
                <FieldGroup label="Attack Reach">
                  <input
                    className="cpf-input"
                    ref={register}
                    name="attackReach"
                    placeholder="e.g. 310cm"
                  />
                </FieldGroup>
                <FieldGroup label={selectedSport === 'Track & Field' ? 'Event' : 'Position'}>
                  <Multiselect
                    selectedValues={isEditMode && player.position}
                    options={getOptions()}
                    isObject={false}
                    displayValue="key"
                    showCheckbox
                    closeOnSelect={false}
                    style={multiselectStyleSingle}
                    ref={positionRef}
                    placeholder="Select..."
                  />
                </FieldGroup>
              </div>
            </div>

            {/* ── Media ── */}
            <div className="cpf-section">
              <SectionHeader icon="fa-photo-video" title="Media" subtitle="Videos & player photo" />

              {/* Video links */}
              <div className="cpf-dynamic-list">
                <div className="cpf-dynamic-list__header">
                  <label className="cpf-field__label">
                    <i className="fab fa-youtube" style={{ marginRight: '0.5rem', color: '#cc3d45' }} />
                    YouTube Videos
                  </label>
                  <button
                    type="button"
                    className="cpf-add-btn"
                    onClick={() => (prepend ? prepend({}) : append({}))}
                  >
                    <i className="fas fa-plus" /> Add Video
                  </button>
                </div>
                {fields.map((item, index) => (
                  <div className="cpf-dynamic-list__item" key={item.id}>
                    <input
                      className="cpf-input"
                      ref={register()}
                      name={`youtubeUrl[${index}]`}
                      defaultValue={item.value}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                    <button
                      type="button"
                      className="cpf-remove-btn"
                      onClick={() => remove(index)}
                      title="Remove"
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  </div>
                ))}
                {fields.length === 0 && (
                  <p className="cpf-dynamic-list__empty">No videos added yet.</p>
                )}
              </div>

              {/* Photo upload */}
              <div className="cpf-upload">
                <label className="cpf-field__label" style={{ marginBottom: '0.75rem' }}>
                  <i className="fas fa-camera" style={{ marginRight: '0.5rem', color: '#2e496b' }} />
                  {isEditMode ? 'Update Player Photo' : 'Player Photo'}
                </label>
                <div className="cpf-upload__area">
                  <input
                    type="file"
                    name="image"
                    ref={register}
                    className="cpf-upload__input"
                  />
                  <div className="cpf-upload__placeholder">
                    <i className="fas fa-cloud-upload-alt" />
                    <span>Click to upload or drag & drop</span>
                    <small>JPG, PNG up to 5MB</small>
                  </div>
                </div>
                {isEditMode && (
                  <div className="cpf-upload__preview">
                    <Card.Img
                      variant="top"
                      src={checkIfPhotoExists()}
                      className="cpf-upload__image"
                    />
                    <span className="cpf-upload__caption">Current photo</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ════════════ RIGHT COLUMN — Evaluation ════════════ */}
          <div className="cpf-layout__sidebar">
            <div className="cpf-section cpf-section--eval">
              <SectionHeader icon="fa-clipboard-check" title="Sport Evaluation" subtitle="Rate the player's abilities" />

              <FieldGroup label="Physical Attributes">
                <Multiselect
                  selectedValues={isEditMode && physicalFeatures}
                  options={playerPhysicalFeature}
                  groupBy="section"
                  displayValue="key"
                  showCheckbox
                  selectionLimit={4}
                  isObject
                  closeOnSelect={false}
                  style={multiselectStyle}
                  ref={multiselectPhysicalsRef}
                  placeholder="Select up to 4..."
                />
                <CustomEntries
                  fields={customPhysicalsFields}
                  append={customPhysicalsAppend}
                  remove={customPhysicalsRemove}
                  register={register}
                  name="customPhysicals"
                  placeholder="e.g. Vertical jump: 70 cm"
                />
              </FieldGroup>

              <FieldGroup label="English Proficiency">
                <Multiselect
                  selectedValues={isEditMode && player.englishSkills}
                  options={playerEnglishSkills}
                  singleSelect
                  isObject={false}
                  displayValue="key"
                  showCheckbox
                  style={multiselectStyleSingle}
                  ref={multiselectEnglishRef}
                  placeholder="Select level..."
                />
              </FieldGroup>

              <FieldGroup label="In-Person Analysis">
                <Multiselect
                  selectedValues={isEditMode && analysisFeatures}
                  options={playerAnalysisFeature}
                  isObject={false}
                  showCheckbox
                  closeOnSelect={false}
                  style={multiselectStyle}
                  ref={multiselectAnalysisRef}
                  placeholder="Select observations..."
                />
                <CustomEntries
                  fields={customAnalysisFields}
                  append={customAnalysisAppend}
                  remove={customAnalysisRemove}
                  register={register}
                  name="customAnalysis"
                  placeholder="e.g. Strong leader on the court"
                />
              </FieldGroup>

              <FieldGroup label="Expected Level of Play">
                <Multiselect
                  selectedValues={isEditMode && player.levelOfPlay}
                  options={levelsOfPlay}
                  isObject={false}
                  displayValue="key"
                  showCheckbox
                  closeOnSelect={false}
                  style={multiselectStyleSingle}
                  ref={multiSelectLevelOfPlayRef}
                  placeholder="Select levels..."
                />
              </FieldGroup>

              <FieldGroup label="Additional Comments">
                <div className="cpf-quill-wrapper">
                  <ReactQuill
                    value={additionalComments}
                    onChange={handleCommentsChange}
                    modules={quillModules}
                    formats={quillFormats}
                    theme="snow"
                    placeholder="Write evaluation notes here..."
                  />
                </div>
              </FieldGroup>
            </div>
          </div>
        </div>

        {/* ─── Submit ─── */}
        <div className="cpf-submit">
          <button type="submit" className="cpf-submit__btn">
            <i className={`fas ${isEditMode ? 'fa-save' : 'fa-plus-circle'}`} />
            {isEditMode ? 'Save Changes' : 'Create Player'}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default CreatePlayerForm;
